// @flow
import React, { Component } from 'react';
import {
  Field,
  type FormProps,
  formValueSelector,
  reduxForm,
} from 'redux-form';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl } from 'react-intl';

import type {
  Catalog,
  CatalogingOptions,
  Dispatch,
  FieldDefinition,
  IngestConfig,
} from 'state/types';
import {
  getCatalogingOptions,
  getFields,
  getIngestConfig,
  getUseConstrainedIngest,
} from 'state/selectors';
import {
  getCatalogProperties as getCatalogPropertiesCommand,
  getFields as getFieldsCommand,
  setIngestConfig,
  updateCatalogingOptions,
} from 'state/commands/catalogCommands';
import CheckRadio from 'view/Forms/BulmaCheckRadio';
import { bindActionCreatorsForm } from 'util/safeDispatch';
import localizedStrings from 'util/localizedStrings';
import { CATALOG_PROPERTY_KEY } from 'util/constants';
import logger from 'portfolio-shared/util/logging';
import ManagedUploadFields from './CatalogManagedUploadFields';

type Props = {
  catalog: Catalog,
  catalogingOptions: CatalogingOptions,
  fieldDefs: FieldDefinition[],
  getCatalogPropertiesCommand: typeof getCatalogPropertiesCommand,
  getFieldsCommand: typeof getFieldsCommand,
  ingestConfig: IngestConfig,
  setIngestConfig: typeof setIngestConfig,
  updateCatalogingOptions: typeof updateCatalogingOptions,
  useManagedUpload: boolean,
} & FormProps;

class CatalogManagedUploadForm extends Component<Props> {
  submit: Function;

  constructor() {
    super();

    this.submit = this.submit.bind(this);
  }
  async componentDidMount() {
    if (this.props.catalog) {
      const { catalogId } = this.props.catalog;
      await Promise.all([
        this.props.getCatalogPropertiesCommand(catalogId, [
          CATALOG_PROPERTY_KEY.CATALOGING_OPTIONS,
          CATALOG_PROPERTY_KEY.INGEST_CONFIG,
          CATALOG_PROPERTY_KEY.CATALOG_DETAILS,
        ]),
        this.props.getFieldsCommand(catalogId),
      ]);
    }
  }

  async submit(values) {
    const { catalog, catalogingOptions } = this.props;
    logger.debug({ managedIngest: values });
    await this.props.setIngestConfig(
      catalog.catalogId,
      {
        ...catalogingOptions,
        useConstrainedIngest: values.useManagedUpload,
      },
      {
        headerText: values.formHeader,
        fields: values.formFields,
      }
    );
  }

  render() {
    const {
      fieldDefs,
      handleSubmit,
      intl: { formatMessage },
      invalid,
      pristine,
      submitting,
      useManagedUpload,
    } = this.props;
    return (
      <div
        className="portfolio-form-container page-contents-textured"
        data-test-name="managed-upload-page"
      >
        <form className="box" onSubmit={handleSubmit(this.submit)}>
          <div className="field">
            <div className="control">
              <Field
                className="is-medium"
                component={CheckRadio}
                id="useManagedUpload"
                label={formatMessage(
                  localizedStrings.CATALOG_INGEST_USE_INGEST_LABEL
                )}
                name="useManagedUpload"
                type="checkbox"
              />
            </div>
          </div>

          <div className="vertical-spacer-large" />

          <div className="field">
            <label className="label">
              <FormattedMessage
                {...localizedStrings.CATALOG_INGEST_FORM_HEADER_LABEL}
              />
            </label>
            <div className="control" data-test-name="form-header">
              <Field
                className="textarea"
                component="textarea"
                disabled={!useManagedUpload}
                name="formHeader"
                rows={2}
                type="textarea"
              />
            </div>
            <p className="help">
              <FormattedMessage
                {...localizedStrings.CATALOG_INGEST_HEADER_HINT_TEXT}
              />
            </p>
          </div>

          <div className="vertical-spacer-large" />

          <div className="field">
            <Field
              component={ManagedUploadFields}
              disabled={!useManagedUpload}
              name="formFields"
              fieldDefs={fieldDefs}
            />
          </div>

          <div className="vertical-spacer-large" />

          <div className="field is-grouped is-pulled-right">
            <div className="control">
              <button
                className="button is-dark is-outlined"
                disabled={pristine || submitting}
                onClick={this.props.reset}
              >
                <FormattedMessage {...localizedStrings.REVERT} />
              </button>
            </div>
            <div className="control">
              <button
                className={`button is-primary ${
                  submitting ? 'is-loading' : ''
                }`}
                data-test-name="apply"
                disabled={pristine || invalid}
                type="submit"
              >
                <FormattedMessage {...localizedStrings.APPLY} />
              </button>
            </div>
          </div>

          <div className="vertical-spacer-xlarge" />
        </form>
      </div>
    );
  }
}

const FORM_NAME = 'CatalogManagedUploadForm';
const form = reduxForm({
  enableReinitialize: true,
  form: FORM_NAME,
})(injectIntl(CatalogManagedUploadForm));

const uploadFormSelector = formValueSelector(FORM_NAME);

export default connect(
  state => ({
    catalogingOptions: getCatalogingOptions(state),
    fieldDefs: getFields(state),
    initialValues: {
      formHeader: getIngestConfig(state).headerText,
      useManagedUpload: getUseConstrainedIngest(state),
      formFields: getIngestConfig(state).fields,
    },
    ingestConfig: getIngestConfig(state),
    useManagedUpload: uploadFormSelector(state, 'useManagedUpload'),
  }),
  (dispatch: Dispatch) =>
    bindActionCreatorsForm({
      getCatalogPropertiesCommand,
      getFieldsCommand,
      setIngestConfig,
      updateCatalogingOptions,
    })
)(form);



// WEBPACK FOOTER //
// ./src/view/Pages/CatalogsPage/ManagedFields/CatalogManagedUploadForm.js